import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MapPin, Building2, ChevronDown, CreditCard, ArrowRight, Pill, Loader, X } from 'lucide-react';
import { MemberContext } from '../App';
import axios from 'axios';
import { getPharmacyLogo, getPharmacyColor, getPharmacyShortName } from '../utils/pharmacyLogos';
import { TelehealthSection } from '../components/TelehealthPromo';

const Pharmacies = () => {
  const navigate = useNavigate();
  const { rxgoMemberId, setRxgoMemberId } = useContext(MemberContext);
  
  // Search states
  const [drugQuery, setDrugQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  
  // Selected drug states
  const [selectedDrug, setSelectedDrug] = useState(null);
  const [drugGroups, setDrugGroups] = useState([]);
  const [selectedDrugGroup, setSelectedDrugGroup] = useState(null);
  const [dosages, setDosages] = useState([]);
  const [selectedDosage, setSelectedDosage] = useState(null);
  const [quantity, setQuantity] = useState(30);
  const [loadingDosages, setLoadingDosages] = useState(false);
  
  // Pharmacy filter
  const [selectedPharmacy, setSelectedPharmacy] = useState('');
  const [showPharmacyDropdown, setShowPharmacyDropdown] = useState(false);
  const [showDosageDropdown, setShowDosageDropdown] = useState(false);
  const [showDrugTypeDropdown, setShowDrugTypeDropdown] = useState(false);
  
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const searchRef = useRef(null);
  const searchTimeout = useRef(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowResults(false);
      }
      if (!e.target.closest('.pharmacy-dropdown')) {
        setShowPharmacyDropdown(false);
      }
      if (!e.target.closest('.dosage-dropdown')) {
        setShowDosageDropdown(false);
      }
      if (!e.target.closest('.drugtype-dropdown')) {
        setShowDrugTypeDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const colors = {
    primary: '#2055df',
    primaryDark: '#1a44b8',
    secondary: '#0f172a',
    accent: '#06b6d4',
    success: '#10b981',
    bg: '#f8fafc',
    white: '#ffffff',
    gray100: '#f1f5f9',
    gray200: '#e2e8f0',
    gray400: '#94a3b8',
    gray500: '#64748b',
    gray600: '#475569',
    gray700: '#334155',
    gray800: '#1e293b',
    gray900: '#0f172a'
  };

  // Pharmacy list for filter
  const pharmacyOptions = [
    { value: '', label: 'All Pharmacies' },
    { value: 'CVS', label: 'CVS Pharmacy' },
    { value: 'Walgreens', label: 'Walgreens' },
    { value: 'Walmart', label: 'Walmart' },
    { value: 'Rite Aid', label: 'Rite Aid' },
    { value: 'Kroger', label: 'Kroger' },
    { value: 'Costco', label: 'Costco' },
    { value: 'Sam\'s Club', label: 'Sam\'s Club' },
    { value: 'Target', label: 'Target' },
    { value: 'Publix', label: 'Publix' },
    { value: 'H-E-B', label: 'H-E-B' },
    { value: 'Safeway', label: 'Safeway' },
    { value: 'Albertsons', label: 'Albertsons' },
    { value: 'Giant', label: 'Giant' },
    { value: 'Food Lion', label: 'Food Lion' },
    { value: 'Winn-Dixie', label: 'Winn-Dixie' },
    { value: 'Meijer', label: 'Meijer' },
    { value: 'Wegmans', label: 'Wegmans' },
    { value: 'Harris Teeter', label: 'Harris Teeter' }
  ];

  // Featured pharmacies for display grid
  const featuredPharmacies = [
    { name: 'CVS Pharmacy', locations: '9,900+' },
    { name: 'Walgreens', locations: '8,900+' },
    { name: 'Walmart Pharmacy', locations: '5,000+' },
    { name: 'Rite Aid', locations: '2,400+' },
    { name: 'Kroger Pharmacy', locations: '2,200+' },
    { name: 'Costco Pharmacy', locations: '590+' },
    { name: 'Sam\'s Club Pharmacy', locations: '600+' },
    { name: 'Target Pharmacy', locations: '1,900+' },
    { name: 'Publix Pharmacy', locations: '1,200+' },
    { name: 'H-E-B Pharmacy', locations: '350+' },
    { name: 'Safeway Pharmacy', locations: '900+' },
    { name: 'Albertsons Pharmacy', locations: '1,700+' }
  ];

  // Search drugs as user types
  const handleDrugSearch = async (query) => {
    setDrugQuery(query);
    
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    
    if (query.length < 2) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }
    
    searchTimeout.current = setTimeout(async () => {
      setSearching(true);
      try {
        const response = await axios.get(`/api/drugs/search/${encodeURIComponent(query)}`, {
          headers: { 'rxgo-member-id': rxgoMemberId }
        });
        if (response.data.memberId) setRxgoMemberId(response.data.memberId);
        setSearchResults(response.data.drugs || []);
        setShowResults(true);
      } catch (error) {
        console.error('Search error:', error);
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
  };

  // Select a drug from search results
  const handleSelectDrug = async (drug) => {
    setSelectedDrug(drug);
    setDrugQuery(drug.drugName || drug.name);
    setShowResults(false);
    
    // Fetch dosages for selected drug
    setLoadingDosages(true);
    try {
      const response = await axios.get(`/api/drugs/dosages/${encodeURIComponent(drug.drugName || drug.name)}`, {
        headers: { 'rxgo-member-id': rxgoMemberId }
      });
      
      if (response.data.memberId) setRxgoMemberId(response.data.memberId);
      
      const groups = response.data.drugGroups || [];
      const allDosages = response.data.dosages || [];
      
      if (groups.length > 0) {
        setDrugGroups(groups);
        const firstGroup = groups.find(g => g.generic) || groups[0];
        setSelectedDrugGroup(firstGroup);
        setDosages(firstGroup.dosages || []);
        if (firstGroup.dosages?.length > 0) {
          setSelectedDosage(firstGroup.dosages[0]);
          setQuantity(firstGroup.dosages[0].defaultQuantity || 30);
        }
      } else if (allDosages.length > 0) {
        setDosages(allDosages);
        setSelectedDosage(allDosages[0]);
        setQuantity(allDosages[0].defaultQuantity || 30);
      }
    } catch (error) {
      console.error('Error fetching dosages:', error);
    } finally {
      setLoadingDosages(false);
    }
  };

  // Handle drug group change (Generic vs Brand)
  const handleDrugGroupChange = (group) => {
    setSelectedDrugGroup(group);
    setShowDrugTypeDropdown(false);
    setDosages(group.dosages || []);
    if (group.dosages?.length > 0) {
      setSelectedDosage(group.dosages[0]);
      setQuantity(group.dosages[0].defaultQuantity || 30);
    }
  };

  // Compare prices - navigate to drug page
  const handleComparePrices = () => {
    if (!selectedDrug) {
      if (drugQuery.trim()) {
        // Navigate with just drug name
        const url = `/drug/${encodeURIComponent(drugQuery.trim().toLowerCase())}${selectedPharmacy ? `?pharmacy=${encodeURIComponent(selectedPharmacy)}` : ''}`;
        navigate(url);
      }
      return;
    }
    
    // Navigate with full details
    const params = new URLSearchParams();
    if (selectedDosage?.ndc) params.set('ndc', selectedDosage.ndc);
    if (quantity) params.set('qty', quantity);
    if (selectedPharmacy) params.set('pharmacy', selectedPharmacy);
    
    const drugName = selectedDrugGroup?.drugName || selectedDrug.drugName || selectedDrug.name;
    navigate(`/drug/${encodeURIComponent(drugName.toLowerCase())}${params.toString() ? '?' + params.toString() : ''}`);
  };

  const formatDosage = (drug) => {
    if (!drug) return 'Select Dosage';
    if (drug.dosage) return drug.dosage.toUpperCase();
    if (drug.strength && drug.form) return `${drug.strength} ${drug.form}`.toUpperCase();
    if (drug.dosageForm && drug.strength) return `${drug.strength} ${drug.dosageForm}`.toUpperCase();
    return drug.dosageForm?.toUpperCase() || drug.form?.toUpperCase() || 'TABLET';
  };

  const PharmacyLogo = ({ name, size = 48 }) => {
    const [imgError, setImgError] = useState(false);
    const brandColor = getPharmacyColor(name);
    const shortName = getPharmacyShortName(name);
    const logoUrl = getPharmacyLogo(name);
    
    // Try Clearbit logo first
    if (logoUrl && !imgError) {
      return (
        <img
          src={logoUrl}
          alt={name}
          onError={() => setImgError(true)}
          style={{
            width: size,
            height: size,
            borderRadius: 12,
            objectFit: 'contain',
            background: 'white',
            padding: size > 40 ? 6 : 4,
            border: '1px solid #e2e8f0',
            flexShrink: 0
          }}
        />
      );
    }
    
    // Fallback to colored badge
    return (
      <div style={{
        width: size,
        height: size,
        borderRadius: 12,
        background: brandColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size * 0.35,
        fontWeight: 800,
        color: 'white',
        flexShrink: 0
      }}>
        {shortName}
      </div>
    );
  };

  const stats = [
    { value: '67,000+', label: 'Pharmacies' },
    { value: '80%', label: 'Max Savings' },
    { value: '$0', label: 'Card Cost' },
    { value: '1M+', label: 'Users' }
  ];

  return (
    <div style={{ fontFamily: "'Inter', -apple-system, sans-serif" }}>
      {/* Hero with Search */}
      <section style={{
        background: colors.primary,
        padding: isMobile ? '48px 16px' : '80px 24px',
        textAlign: 'center',
        marginTop: 0
      }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <h1 style={{ 
            fontSize: isMobile ? 28 : 48, 
            fontWeight: 800, 
            color: 'white', 
            marginBottom: 16,
            lineHeight: 1.2
          }}>
            Save with our <span style={{ color: colors.accent }}>Rx Pharmacy</span> Coupon
          </h1>
          <p style={{ 
            fontSize: isMobile ? 15 : 20, 
            color: 'rgba(255,255,255,0.85)', 
            marginBottom: isMobile ? 28 : 40,
            lineHeight: 1.6,
            padding: '0 8px'
          }}>
            Simply enter your dosage and quantity below and click "Compare Prices"
          </p>

          {/* Search Box */}
          <div style={{
            background: 'white',
            borderRadius: 16,
            padding: isMobile ? 16 : 20,
            maxWidth: 800,
            margin: '0 auto',
            boxShadow: '0 20px 40px rgba(0,0,0,0.2)'
          }}>
            {/* Drug Search Input */}
            <div ref={searchRef} style={{ position: 'relative', marginBottom: 16 }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                background: colors.gray100,
                borderRadius: 10,
                padding: '0 14px',
                border: showResults ? `2px solid ${colors.primary}` : '2px solid transparent'
              }}>
                <Pill size={20} style={{ color: colors.primary, flexShrink: 0 }} />
                <input
                  type="text"
                  value={drugQuery}
                  onChange={(e) => handleDrugSearch(e.target.value)}
                  onFocus={() => searchResults.length > 0 && setShowResults(true)}
                  placeholder="Enter drug name (e.g., Metformin, Lisinopril)"
                  style={{
                    flex: 1,
                    border: 'none',
                    outline: 'none',
                    fontSize: 16,
                    padding: '14px 0',
                    background: 'transparent',
                    color: colors.gray800,
                    minWidth: 0
                  }}
                />
                {searching && <Loader size={18} style={{ color: colors.primary, animation: 'spin 1s linear infinite' }} />}
                {drugQuery && !searching && (
                  <button
                    onClick={() => {
                      setDrugQuery('');
                      setSelectedDrug(null);
                      setDosages([]);
                      setSelectedDosage(null);
                      setSearchResults([]);
                    }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
                  >
                    <X size={18} style={{ color: colors.gray400 }} />
                  </button>
                )}
              </div>
              
              {/* Search Results Dropdown */}
              {showResults && searchResults.length > 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  background: 'white',
                  borderRadius: 10,
                  boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                  marginTop: 8,
                  maxHeight: 300,
                  overflow: 'auto',
                  zIndex: 100
                }}>
                  {searchResults.map((drug, i) => (
                    <button
                      key={i}
                      onClick={() => handleSelectDrug(drug)}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: 'none',
                        background: 'none',
                        textAlign: 'left',
                        cursor: 'pointer',
                        borderBottom: i < searchResults.length - 1 ? `1px solid ${colors.gray100}` : 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10
                      }}
                      onMouseEnter={(e) => e.target.style.background = colors.gray50}
                      onMouseLeave={(e) => e.target.style.background = 'none'}
                    >
                      <Pill size={16} style={{ color: colors.primary }} />
                      <span style={{ color: colors.gray800, fontSize: 15 }}>
                        {drug.drugName || drug.name}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Filters Row */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
              gap: 12,
              marginBottom: 16
            }}>
              {/* Drug Type (Generic/Brand) - Only show if drug groups available */}
              {drugGroups.length > 1 && (
                <div className="drugtype-dropdown" style={{ position: 'relative' }}>
                  <button
                    onClick={() => setShowDrugTypeDropdown(!showDrugTypeDropdown)}
                    style={{
                      width: '100%',
                      padding: '12px 14px',
                      background: colors.gray100,
                      border: 'none',
                      borderRadius: 10,
                      fontSize: 14,
                      color: colors.gray800,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 8
                    }}
                  >
                    <span style={{ 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis', 
                      whiteSpace: 'nowrap',
                      textTransform: 'capitalize'
                    }}>
                      {selectedDrugGroup?.drugName || 'Select Type'}
                      {selectedDrugGroup?.generic ? ' (Generic)' : selectedDrugGroup ? ' (Brand)' : ''}
                    </span>
                    <ChevronDown size={16} style={{ flexShrink: 0, color: colors.gray500 }} />
                  </button>
                  
                  {showDrugTypeDropdown && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      background: 'white',
                      borderRadius: 10,
                      boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                      marginTop: 4,
                      zIndex: 50,
                      overflow: 'hidden'
                    }}>
                      {drugGroups.map((group, i) => (
                        <button
                          key={i}
                          onClick={() => handleDrugGroupChange(group)}
                          style={{
                            width: '100%',
                            padding: '12px 14px',
                            border: 'none',
                            background: selectedDrugGroup === group ? colors.gray100 : 'white',
                            textAlign: 'left',
                            cursor: 'pointer',
                            fontSize: 14,
                            color: colors.gray800,
                            textTransform: 'capitalize'
                          }}
                        >
                          {group.drugName} {group.generic ? '(Generic)' : '(Brand)'}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Dosage Dropdown */}
              <div className="dosage-dropdown" style={{ position: 'relative' }}>
                <button
                  onClick={() => dosages.length > 0 && setShowDosageDropdown(!showDosageDropdown)}
                  disabled={dosages.length === 0}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    background: colors.gray100,
                    border: 'none',
                    borderRadius: 10,
                    fontSize: 14,
                    color: dosages.length === 0 ? colors.gray400 : colors.gray800,
                    cursor: dosages.length === 0 ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8
                  }}
                >
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {loadingDosages ? 'Loading...' : formatDosage(selectedDosage)}
                  </span>
                  <ChevronDown size={16} style={{ flexShrink: 0, color: colors.gray500 }} />
                </button>
                
                {showDosageDropdown && dosages.length > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    background: 'white',
                    borderRadius: 10,
                    boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                    marginTop: 4,
                    maxHeight: 200,
                    overflow: 'auto',
                    zIndex: 50
                  }}>
                    {dosages.map((d, i) => (
                      <button
                        key={i}
                        onClick={() => {
                          setSelectedDosage(d);
                          setQuantity(d.defaultQuantity || 30);
                          setShowDosageDropdown(false);
                        }}
                        style={{
                          width: '100%',
                          padding: '12px 14px',
                          border: 'none',
                          background: selectedDosage === d ? colors.gray100 : 'white',
                          textAlign: 'left',
                          cursor: 'pointer',
                          fontSize: 14,
                          color: colors.gray800
                        }}
                      >
                        {formatDosage(d)}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Quantity Input */}
              <div>
                <input
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(parseInt(e.target.value) || 30)}
                  min="1"
                  max="999"
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    background: colors.gray100,
                    border: 'none',
                    borderRadius: 10,
                    fontSize: 14,
                    color: colors.gray800,
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  placeholder="Qty"
                />
              </div>

              {/* Pharmacy Filter */}
              <div className="pharmacy-dropdown" style={{ position: 'relative' }}>
                <button
                  onClick={() => setShowPharmacyDropdown(!showPharmacyDropdown)}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    background: colors.gray100,
                    border: 'none',
                    borderRadius: 10,
                    fontSize: 14,
                    color: colors.gray800,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8, overflow: 'hidden' }}>
                    <Building2 size={16} style={{ color: colors.primary, flexShrink: 0 }} />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {selectedPharmacy || 'All Pharmacies'}
                    </span>
                  </span>
                  <ChevronDown size={16} style={{ flexShrink: 0, color: colors.gray500 }} />
                </button>
                
                {showPharmacyDropdown && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    background: 'white',
                    borderRadius: 10,
                    boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                    marginTop: 4,
                    maxHeight: 250,
                    overflow: 'auto',
                    zIndex: 50
                  }}>
                    {pharmacyOptions.map((p, i) => (
                      <button
                        key={i}
                        onClick={() => {
                          setSelectedPharmacy(p.value);
                          setShowPharmacyDropdown(false);
                        }}
                        style={{
                          width: '100%',
                          padding: '12px 14px',
                          border: 'none',
                          background: selectedPharmacy === p.value ? colors.gray100 : 'white',
                          textAlign: 'left',
                          cursor: 'pointer',
                          fontSize: 14,
                          color: colors.gray800,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10
                        }}
                      >
                        {p.value && <PharmacyLogo name={p.label} size={24} />}
                        {p.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Compare Prices Button */}
            <button
              onClick={handleComparePrices}
              disabled={!drugQuery.trim()}
              style={{
                width: '100%',
                padding: '16px 24px',
                background: drugQuery.trim() ? colors.primary : colors.gray300,
                color: 'white',
                border: 'none',
                borderRadius: 12,
                fontSize: 16,
                fontWeight: 600,
                cursor: drugQuery.trim() ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10
              }}
            >
              Compare Prices <ArrowRight size={20} />
            </button>
          </div>
        </div>
      </section>

      {/* Accepted Pharmacies Banner */}
      <section style={{ 
        background: 'white', 
        padding: isMobile ? '36px 16px' : '48px 24px',
        textAlign: 'center'
      }}>
        <p style={{ 
          fontSize: isMobile ? 16 : 20, 
          color: colors.gray700, 
          marginBottom: 24 
        }}>
          Accepted at over <strong style={{ color: colors.primary }}>67,000 pharmacies</strong> nationwide including
        </p>
        
        {/* Pharmacy Logos Row */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: isMobile ? 16 : 24,
          maxWidth: 1000,
          margin: '0 auto'
        }}>
          {['CVS Pharmacy', 'Walgreens', 'Walmart Pharmacy', 'Rite Aid', 'Kroger Pharmacy', 'Costco Pharmacy', 'Target Pharmacy', 'Publix Pharmacy'].map((name, i) => (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
              <PharmacyLogo name={name} size={isMobile ? 48 : 56} />
              <span style={{ fontSize: isMobile ? 11 : 12, color: colors.gray600, fontWeight: 500 }}>
                {name.replace(' Pharmacy', '')}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* Stats */}
      <section style={{ background: colors.bg, padding: isMobile ? '36px 16px' : '48px 24px' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: isMobile ? 12 : 32
          }}>
            {stats.map((stat, i) => (
              <div key={i} style={{ textAlign: 'center' }}>
                <div style={{ 
                  fontSize: isMobile ? 22 : 36, 
                  fontWeight: 800, 
                  color: colors.primary, 
                  marginBottom: 4 
                }}>
                  {stat.value}
                </div>
                <div style={{ 
                  fontSize: isMobile ? 11 : 14, 
                  color: colors.gray600 
                }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* All Pharmacies Grid */}
      <section style={{ background: 'white', padding: isMobile ? '48px 16px' : '80px 24px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: isMobile ? 28 : 48 }}>
            <h2 style={{ 
              fontSize: isMobile ? 24 : 36, 
              fontWeight: 700, 
              color: colors.gray900, 
              marginBottom: 12 
            }}>
              Major Pharmacy Partners
            </h2>
            <p style={{ 
              fontSize: isMobile ? 15 : 18, 
              color: colors.gray600 
            }}>
              Your discount card works at all these locations and more
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: isMobile ? 12 : 20
          }}>
            {featuredPharmacies.map((pharmacy, i) => (
              <div
                key={i}
                onClick={() => {
                  setSelectedPharmacy(pharmacy.name.replace(' Pharmacy', ''));
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                style={{
                  background: colors.bg,
                  padding: isMobile ? 16 : 24,
                  borderRadius: 12,
                  border: `1px solid ${colors.gray200}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: isMobile ? 12 : 16,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = colors.primary;
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(32,85,223,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = colors.gray200;
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <PharmacyLogo name={pharmacy.name} size={isMobile ? 44 : 48} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h3 style={{ 
                    fontSize: isMobile ? 14 : 16, 
                    fontWeight: 600, 
                    color: colors.gray900, 
                    marginBottom: 4,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {pharmacy.name}
                  </h3>
                  <p style={{ 
                    fontSize: isMobile ? 12 : 13, 
                    color: colors.gray500, 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 4,
                    margin: 0
                  }}>
                    <MapPin size={12} /> {pharmacy.locations} locations
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Telehealth */}
      <TelehealthSection />

      {/* CTA */}
      <section style={{
        background: colors.primary,
        padding: isMobile ? '56px 16px' : '80px 24px',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <h2 style={{ 
            fontSize: isMobile ? 26 : 40, 
            fontWeight: 700, 
            color: 'white', 
            marginBottom: 16 
          }}>
            Get Your Free Card Today
          </h2>
          <p style={{ 
            fontSize: isMobile ? 15 : 18, 
            color: 'rgba(255,255,255,0.85)', 
            marginBottom: isMobile ? 28 : 32 
          }}>
            No signup required. Start saving immediately.
          </p>
          <button
            onClick={() => navigate('/card')}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
              padding: isMobile ? '16px 28px' : '18px 40px',
              background: 'white',
              color: colors.primary,
              border: 'none',
              borderRadius: 12,
              fontSize: isMobile ? 16 : 18,
              fontWeight: 600,
              cursor: 'pointer',
              width: '100%',
              maxWidth: 300,
              margin: '0 auto'
            }}
          >
            <CreditCard size={22} />
            Get My Free Card
          </button>
        </div>
      </section>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default Pharmacies;